import LoginView from '@components/auth/LoginView'
import CartSidebarView from '@components/cart/CartSidebarView'
import CheckoutSidebarView from '@components/checkout/CheckoutSidebarView'
import { CheckoutProvider } from '@components/checkout/context'
import PaymentMethodView from '@components/checkout/PaymentMethodView'
import ShippingView from '@components/checkout/ShippingView'
import { Footer, Navbar } from '@components/common'
import { MenuSidebarView } from '@components/common/UserNav'
import MobileProfileSidebar from '@components/profile/MobileProfileSidebar'
import ProfileSidebarView from '@components/profile/ProfileSidebarView'
import SearchBarView from '@components/common/Searchbar'
import ServicePortalSidebarView from '@components/profile/ServicePortalSidebar'
import { LoadingDots, Sidebar } from '@components/ui'
import { useUI } from '@components/ui/context'
import { CommerceProvider } from '@framework'
import cn from 'clsx'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import s from './Layout.module.css'
import { useMemo } from 'react'
import Script from "next/script";

const Loading = () => (
  <div className="w-80 h-80 flex items-center text-center justify-center p-3">
    <LoadingDots />
  </div>
)

const dynamicProps = {
  loading: Loading,
}

const SignUpView = dynamic(() => import('@components/auth/SignUpView'), {
  ...dynamicProps,
})

const ForgotPassword = dynamic(
  () => import('@components/auth/ForgotPassword'),
  {
    ...dynamicProps,
  }
)

const FeatureBar = dynamic(() => import('@components/common/FeatureBar'), {
  ...dynamicProps,
})

const Modal = dynamic(() => import('@components/ui/Modal'), {
  ...dynamicProps,
  ssr: false,
})

interface Props {
  pageProps: {
    categories?: any
    mainNav?: any
    footer?: any
    fit?: boolean
    announcement?: {
      data: {
       Title: string,
       description: string,
       fullWidth: string,
       slices: any[],
      }
    }
  }
}

const ModalView: React.FC<{ modalView: string; closeModal(): any }> = ({
  modalView,
  closeModal,
}) => {
  return (
    <Modal onClose={closeModal}>
      {modalView === 'LOGIN_VIEW' && <LoginView />}
      {modalView === 'SIGNUP_VIEW' && <SignUpView />}
      {modalView === 'FORGOT_VIEW' && <ForgotPassword />}
    </Modal>
  )
}

const ModalUI: React.FC = () => {
  const { displayModal, closeModal, modalView } = useUI()
  return displayModal ? (
    <ModalView modalView={modalView} closeModal={closeModal} />
  ) : null
}

const SidebarView: React.FC<{
  sidebarView: string
  closeSidebar(): any
  menuItems: any
}> = ({ sidebarView, closeSidebar, menuItems }) => {
  let slideInFrom = 'slideInFromRight'
  let classNames = 'w-full'
  if (sidebarView === 'MOBILE_MENU_VIEW') slideInFrom = 'slideInFromLeft'
  if (sidebarView === 'CART_VIEW') classNames = 'w-full md:w-92'
  if (sidebarView === 'PROFILE_SIDEBAR_VIEW') classNames = 'w-full md:w-92'
  if (sidebarView === 'SERVICE_PORTAL_VIEW') classNames = 'w-full md:w-92'
  if (sidebarView === 'MOBILE_PROFILE_SIDEBAR_VIEW')
    slideInFrom = 'slideInFromBottom'
  if (sidebarView === 'SEARCH_VIEW') slideInFrom = 'slideInFromTop'
  return (
    <Sidebar
      onClose={closeSidebar}
      slideInFromClassName={slideInFrom}
      classNames={classNames}
    >
      {sidebarView === 'PROFILE_SIDEBAR_VIEW' && (
        <ProfileSidebarView onClose={closeSidebar} />
      )}
      {sidebarView === 'SEARCH_VIEW' && (
        <SearchBarView onClose={closeSidebar} />
      )}
      {sidebarView === 'MOBILE_PROFILE_SIDEBAR_VIEW' && (
        <MobileProfileSidebar onClose={closeSidebar} title="Your Profile" />
      )}
      {sidebarView === 'CART_VIEW' && (
        <CartSidebarView onClose={closeSidebar} />
      )}
      {sidebarView === 'SHIPPING_VIEW' && <ShippingView />}
      {sidebarView === 'PAYMENT_VIEW' && <PaymentMethodView />}
      {sidebarView === 'CHECKOUT_VIEW' && <CheckoutSidebarView />}
      {sidebarView === 'MOBILE_MENU_VIEW' && (
        <MenuSidebarView menuItems={menuItems} onClose={closeSidebar} />
      )}
      {sidebarView === 'SERVICE_PORTAL_VIEW' && (
        <ServicePortalSidebarView onClose={closeSidebar} />
      )}
    </Sidebar>
  )
}

const SidebarUI: React.FC<{ menuItems: any }> = ({ menuItems }) => {
  const { displaySidebar, closeSidebar, sidebarView, setSideBarIsClosing } =
    useUI()

  const handleClose = () => {
    setSideBarIsClosing(true)
    setTimeout(function () {
      setSideBarIsClosing(false)
      closeSidebar()
    }, 200)
  }

  return displaySidebar ? (
    <SidebarView
      menuItems={menuItems}
      sidebarView={sidebarView}
      closeSidebar={handleClose}
    />
  ) : null
}

const Layout: React.FC<Props> = ({ children, pageProps }) => {

  const { locale = 'en-AU' } = useRouter()

  const announcement = useMemo(() => pageProps.announcement && ({
    title: pageProps.announcement?.data?.Title,
    description: pageProps.announcement?.data?.description,
  }), [pageProps.announcement]);

  return (
    <>
      <CommerceProvider locale={locale}>
        <div className={cn(s.root)}>
          <Navbar links={[]} navItems={pageProps?.mainNav} announcement={announcement} />
          <main className="fit">{children}</main>
          <Footer footer={pageProps?.footer} />
          <CheckoutProvider>
            <SidebarUI menuItems={pageProps?.mainNav} />
          </CheckoutProvider>
        </div>
      </CommerceProvider>

      <Script
        src="https://www.googletagmanager.com/gtag/js?id=UA-25550593-1"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'UA-25550593-1');
        `}
      </Script> 

      <Script
        id="hotjar"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:2257502,hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.defer=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
              `
        }}
      />
    </>
  )
}

export default Layout
